/**
 * VRG-545 — the "ROI submitted" column, as a COUNT of the calculator fields the visitor
 * actually filled in ("6/10") rather than a yes/no.
 *
 * ## Why a count replaced "Submitted"
 *
 * The boolean answered a question nobody was asking. Every row on this grid that carries an
 * ROI submission said "Submitted", so the column separated nothing and ranked nothing — and
 * the thing a reviewer working this queue actually wants is how much of themselves a lead
 * put into the form. Somebody who moved seven controls is a different lead from somebody who
 * typed an email and pressed the button on our own seeded numbers, and the old column drew
 * them identically.
 *
 * ## THE PILL IS `ProgressPill`, NOT A NEW LOZENGE
 *
 * `ProgressPill` is this app's "N of M" (working-conventions §28/§47): the sidebar Home row
 * and the setup checklist header already use it, and its own docblock frames it as PASSIVE
 * STATUS — "a number that changes" — which is exactly this. A second N/M treatment tuned to
 * look similar is the drift §36 exists to stop, and it would arrive with a different radius
 * and a different tint on the one screen where both could be seen in a week.
 *
 * Its pulse-on-change is inert here and harmless: it skips the first value, and a grid cell
 * remounts on scroll (so `previous.current` is null again), which means it can only fire if
 * a visible row's count genuinely changes under a refetch.
 *
 * ## THE GLYPH IS A CHECK, AND IT INHERITS COLOUR
 *
 * Founder decision, 2026-09-08, replacing a calculator. The reasoning it overrode — that a
 * check "would read as verified" — was written when the cell was a BOOLEAN, where the check
 * would have been the only mark and would indeed have stood for the figures. It is not that
 * now: the check marks THAT A SUBMISSION EXISTS, which is a fact, and the thing beside it is
 * a COUNT of form completion rather than a value. Nothing in the cell asserts a figure is
 * correct.
 *
 * ⚠️ It takes no `success.main`. GREEN is the register that would claim verification; a check
 * in the row's own text colour reads as "yes, they did this". The caveat about the figures
 * themselves lives in the header tooltip and on every money column, where it belongs.
 *
 * ## Three renderings, because there are three genuinely different states
 *
 * | state | renders | why not something else |
 * |---|---|---|
 * | no ROI submission | em-dash | unchanged; there is nothing to count |
 * | submission WITH `assumptionsTouched` | glyph + `N/10` pill | the count is countable |
 * | submission WITHOUT it | glyph + "Submitted" | ⚠️ see below |
 *
 * ⚠️ THE THIRD ROW IS THE ONE THAT MATTERS. Older submissions do not record which controls
 * were moved, so a count there would rest on comparing all ten values against our own seeded
 * defaults — pure inference, rendered in the identical pill as a recorded one, with nothing
 * on screen to tell them apart. That is precisely the failure this whole surface is built
 * against (`websiteValues.ts`: "an em-dash says the visitor left it blank, not we looked in
 * the wrong place"). So the fallback keeps the old words and says less rather than saying
 * more than it can support.
 */
import React from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { EMPTY_VALUE, Tooltip } from '@repo/ui'
import { CheckIcon } from 'lucide-react'

import { headerWithInfo, INFO_HEADER_ALLOWANCE, ProgressPill } from 'components'
import type { SignupEntry } from 'hooks/api/useSignups'

import { ROI_INPUT_FIELDS } from './websiteFields'
import { roiInputsSupplied, type RoiInputTally } from './websiteValues'

const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

/**
 * The label, once. `headerName` feeds the column menu, the Layout visibility panel and the
 * CSV header; `renderHeader` feeds the rendered header. Two literals is a half-done rename.
 *
 * Kept as "ROI submitted" rather than renamed to "ROI inputs": the column still answers
 * "did they use the calculator", and the count is the finer-grained version of that answer
 * rather than a different question. The header tooltip carries what the number means.
 */
const COLUMN_LABEL = 'ROI submitted'

/**
 * §49 — sized for the HEADER, not the values, and MEASURED rather than estimated.
 *
 * ⚠️ THE COLUMN GREW BECAUSE ITS HEADER DID, not because its cells did. The label is
 * unchanged, but the column gained a SORT ICON (it is sortable now, and a boolean was not
 * worth sorting) and an INFO GLYPH, and both are header chrome. Measured in the browser on
 * `/internal/signups` at 1280×800 by walking the rendered header cell with
 * `getBoundingClientRect`, using the same decomposition `brokerageRevenueColumn` records:
 *
 *     label text ("ROI submitted", 14px Lato)                89.25   measured, not estimated
 *     info glyph + its 4px margin                            18.00   = INFO_HEADER_ALLOWANCE
 *     title-container left padding                            8.00
 *     sort-icon container                                     28.00
 *     header cell padding (10 + 10)                           20.00
 *     ──────────────────────────────────────────────────────────────
 *     minimum                                               163.25   → authored 180, 16.75 slack
 *
 * ⚠️ AND THE PREVIOUS 170 WOULD NOT HAVE CLIPPED — it would have left 6.75px. Said plainly
 * rather than dressed up as a bug fix: this widening buys slack, it does not repair a
 * truncation. The old comment's own estimate ("~93px of text… ~145px before any slack") was
 * close on the text and short on the chrome, having counted an 18px glyph and a 6px margin
 * where the rendered sort container is 28. Verified after the change: the header cell's
 * `scrollWidth` equals its `clientWidth` at 1280 wide, i.e. nothing is truncated.
 */
const HEADER_WIDTH = 180 - INFO_HEADER_ALLOWANCE

/*
  §15 — no em-dash overuse in UI copy. Separate sentences, because this is read by somebody
  deciding how much to believe a number.
*/
const HEADER_NOTE =
  `How many of the ${ROI_INPUT_FIELDS.length} figures the ROI calculator asks for came from the visitor ` +
  "rather than from the site's own starting values. " +
  'They are the three top-line figures (annual units, average retail price and customer acquisition cost) ' +
  'plus the seven assumption sliders. ' +
  'Hover a count to see how much of it is recorded and how much is inferred. ' +
  'Older submissions do not record which controls were moved, so those read "Submitted" instead of a count. ' +
  'A count there would rest entirely on comparing their figures against ours, which is a guess and would look ' +
  'identical to a real count.'

/**
 * The per-row disclosure: what the number is made of.
 *
 * ⚠️ The inferred half's sentence must NOT say "they changed it". It cannot know that. It
 * says what is actually true (the figure differs from our starting value) and names the case
 * that defeats it (someone who deliberately typed our starting value), on exactly the same
 * grounds as `DEFAULT_TAGS`'s "matches default" wording one cell over.
 */
const breakdown = (tally: RoiInputTally): string => {
  const plural = (n: number) => (n === 1 ? 'is' : 'are')
  const parts = [
    `${tally.supplied} of the ${tally.total} figures the calculator asks for came from this visitor.`,
  ]

  if (tally.recorded > 0) {
    parts.push(
      `${tally.recorded} ${plural(tally.recorded)} recorded. The submission names which sliders they moved.`,
    )
  }
  if (tally.inferred > 0) {
    parts.push(
      `${tally.inferred} ${plural(tally.inferred)} inferred, from the figure differing from the site's own starting value. ` +
        'Someone who deliberately typed the starting value is counted here as not having answered.',
    )
  }
  if (tally.supplied === 0) {
    parts.push("Every figure is the site's own starting value.")
  }

  return parts.join(' ')
}

/**
 * The fallback words, in ONE place — the cell renders them and the CSV export writes them,
 * so an export cannot say something the screen does not.
 */
const NO_COUNT_LABEL = 'Submitted'

export const buildRoiInputsColumn = () => ({
  field: 'roi_submitted',
  headerName: COLUMN_LABEL,
  /*
    `string`, NOT `number`, and both halves of that are deliberate.

    MUI's `number` type would right-align the header and the cell, putting this column's
    title out of line with every neighbour for no gain; it would also route the column into
    the Filters drawer's min/max slider, a control nobody designed here.

    Sorting still compares NUMERICALLY: the string column's default comparator is
    `gridStringOrNumberComparator`, which subtracts when the values are numbers and only
    reaches for the collator when they are strings (`gridSortingUtils.js`). `valueGetter`
    below returns `number | null` and never a string, so the collator branch is unreachable
    and "10/10" cannot sort below "7/10" the way a formatted string would.
  */
  type: 'string' as const,
  width: HEADER_WIDTH + INFO_HEADER_ALLOWANCE,
  /*
    Sortable, which the boolean was not. A count RANKS leads by how much of the form they
    engaged with, which is the order a reviewer works this queue in; "Yes" and "No" had two
    values and sorting them told you nothing you could not see.
  */
  sortable: true,
  /*
    NOT filterable, and this is not laziness. The shared Filters drawer renders a `string`
    column as `SearchFilter`, whose option machinery resolves against `row[field]` — and
    there is no `row.roi_submitted`; the value is derived. That yields one blank unlabelled
    checkbox which, ticked, empties the table. `websiteColumns.tsx` documents the same trap
    and dodges it by declaring `multiSelect`; a count has no facet vocabulary to offer, so
    the honest answer here is no filter rather than a broken one.
  */
  filterable: false,
  renderHeader: headerWithInfo(COLUMN_LABEL, HEADER_NOTE),
  /*
    MUI X v7 signature `(value, row, column, apiRef)`. There is no `row.roi_submitted` to
    read — the column is derived from the jsonb blob — so the row is the only input.

    ⚠️ Returns the NUMBER, so the sort is numeric, and `null` for both uncountable states
    (no submission, and a submission with no `assumptionsTouched`). `gridNillComparator`
    groups nulls at one end whichever direction is chosen, which is the right treatment for
    "we cannot rank this" — a 0 would sort among the real counts as the least engaged lead
    on the page and would be indistinguishable from a visitor who genuinely moved nothing.

    The CSV text does NOT come from here — see `csvValue`. A single getter cannot be both a
    numeric sort key and the on-screen words, and forcing one to serve both is how a column
    comes to sort by one thing and read as another.
  */
  valueGetter: (_value: unknown, row: SignupEntry) =>
    row ? roiInputsSupplied(row)?.supplied ?? null : null,
  /*
    THE EXPORT SAYS WHAT THE SCREEN SAYS. `toCsvRows` takes `csvValue(row)` ahead of
    everything else precisely so a derived column can state its own text, and it is needed
    here because the three on-screen states collapse to two numeric values: a row with no
    submission and a row whose count is unknowable are both `null` to the sort, and only the
    row can tell them apart.

    The old getter returned "Yes"/"No", which was already a divergence waiting to happen —
    its own comment called the alternative "the same silent gap the derived-not-stored rule
    is guarding against". A file that reads "Yes" beside a screen reading "6/10" is that gap.
  */
  csvValue: (row: SignupEntry) => {
    if (!row?.roi_submission) return ''
    const tally = roiInputsSupplied(row)
    return tally ? `${tally.supplied}/${tally.total}` : NO_COUNT_LABEL
  },
  renderCell: ({ row }: { row: SignupEntry }) => {
    if (!row?.roi_submission) {
      return (
        <Box sx={cellSx}>
          <Typography variant="body2" color="text.disabled">
            {EMPTY_VALUE}
          </Typography>
        </Box>
      )
    }

    const tally = roiInputsSupplied(row)

    /*
      A check — founder decision, 2026-09-08, replacing the calculator glyph.

      ⚠️ The note it replaces argued a check "would read as verified, and the figures behind
      this count are unverified by construction". That risk is real but it is not what this
      glyph carries here: the check marks THAT A SUBMISSION EXISTS, which is a fact, and the
      thing beside it is a COUNT rather than a value. Nothing in the cell asserts a figure is
      right — `6/10` says how much of the form they filled, and the caveat about the figures
      themselves lives in the header tooltip and on every money column.

      Deliberately INHERITS colour rather than taking `success.main`. Green is the register
      that would claim verification; a check in the row's own text colour reads as "yes,
      they did this", which is all it means.
    */
    const glyph = <CheckIcon size={16} strokeWidth={1.5} />

    if (!tally) {
      return (
        <Tooltip title="This submission predates the site recording which controls the visitor moved, so how much of the form they filled in cannot be counted.">
          <Stack direction="row" spacing={2} alignItems="center" sx={cellSx}>
            {glyph}
            <Typography variant="body2" noWrap>
              {NO_COUNT_LABEL}
            </Typography>
          </Stack>
        </Tooltip>
      )
    }

    return (
      <Tooltip title={breakdown(tally)}>
        <Stack direction="row" spacing={2} alignItems="center" sx={cellSx}>
          {glyph}
          <ProgressPill>
            {tally.supplied}/{tally.total}
          </ProgressPill>
        </Stack>
      </Tooltip>
    )
  },
})
